import { TImprovedContentMarketing } from '../../../../shared/types/contentMarketing';
import * as React from 'react';
import { getConfig, getExperimentVariantFromConfig } from '../../../config';
import { useCallback, useMemo } from 'react';
import { inscreen } from '../../../utils/lazy';
import { UNLEASH_FEATURE_NAMES } from '../../../../shared/unleash/feature-names';

function getPercentage(n: number, size: number) {
    return (n / size) * 100;
}

export type Props = {
    contentMarketing: TImprovedContentMarketing;
    alt: string;
    isMultipleAds: boolean;
    hasImageRadius?: boolean;
    forceDisableZoom?: boolean;
    children?: React.ReactNode;
};
const ImageWithFocus = ({
    contentMarketing,
    alt,
    isMultipleAds,
    hasImageRadius,
    children,
    forceDisableZoom = false,
}: Props) => {
    const [isInView, setIsInView] = React.useState(false);
    const imageRef = React.useRef(null);
    const zoomExperiment = getExperimentVariantFromConfig(
        UNLEASH_FEATURE_NAMES.imageAnimatedZoomContentMarketingExperiment,
    ) as
        | 'control'
        | 'challengerAnimatedZoom10Percent'
        | 'challengerAnimatedZoom30Percent';

    const disableZoom =
        forceDisableZoom || !zoomExperiment || zoomExperiment === 'control';

    React.useEffect(() => {
        if (disableZoom) return;
        let cleanup;
        if (imageRef.current) {
            cleanup = inscreen(
                imageRef.current,
                () => {
                    setIsInView(true);
                },
                {
                    threshold: 0.6,
                },
            );
        }
        return () => {
            if (cleanup) {
                cleanup();
            }
        };
    }, [imageRef, disableZoom]);

    const { imageData } = contentMarketing;

    const objectPosition = useMemo(() => {
        if (imageData?.aoi?.focus) {
            const y = getPercentage(imageData.aoi.focus.y, imageData.height);
            const x = getPercentage(imageData.aoi.focus.x, imageData.width);
            return `${x}% ${y}%`;
        } else {
            return '50% 50%';
        }
    }, [imageData]);

    const cN = useMemo(() => {
        const { deviceType, vertical } = getConfig();
        const isMobile = deviceType === 'mobile';
        const squareAspect =
            isMultipleAds || isMobile
                ? 'aspect-w-3 aspect-h-2'
                : 'aspect-w-16 aspect-h-9';
        const radius = hasImageRadius ? 'rounded-8' : '';
        const border = vertical === 'car' ? 'border-1 border-gray-200' : '';
        return {
            squareAspect,
            radius,
            border,
        };
    }, [isMultipleAds, hasImageRadius]);

    const renderImage = useCallback(() => {
        function getZoomClass() {
            if (disableZoom) return '';
            else {
                if (zoomExperiment === 'challengerAnimatedZoom10Percent')
                    return (
                        'zoom-10-perc' +
                        (isInView ? ' zoom-10-perc-active' : '')
                    );
                else if (zoomExperiment === 'challengerAnimatedZoom30Percent')
                    return (
                        'zoom-30-perc' +
                        (isInView ? ' zoom-30-perc-active' : '')
                    );
                else
                    return (
                        'zoom-10-perc' +
                        (isInView ? ' zoom-10-perc-active' : '')
                    );
            }
        }
        return (
            <div className={cN.squareAspect}>
                <img
                    loading={'lazy'}
                    alt={alt}
                    src={imageData?.url}
                    className={`${getZoomClass()} h-full w-full object-cover ${
                        cN.radius
                    } ${cN.border}`}
                    style={{
                        objectPosition,
                    }}
                />
            </div>
        );
    }, [
        cN,
        alt,
        imageData?.url,
        objectPosition,
        isInView,
        disableZoom,
        zoomExperiment,
    ]);

    return (
        <div
            ref={imageRef}
            className={'overflow-hidden relative h-full w-full'}
        >
            {renderImage()}
            {children}
        </div>
    );
};

export default ImageWithFocus;
